// src/utils/ledgerIds.js
//
// The ledger (accounting-apis, via the Laravel gateway) uses Prisma-style
// primary keys: `fiscal_year_id`, `period_id`, `account_id`, ...
// Older frontend code / mocks sometimes use plain `id` instead.
//
// Rule for the whole frontend: resolve once at the API boundary, then every
// component only ever touches `.id`.
//
//   const years = normalizeFiscalYears(unwrapList(res)); // each has .id
//   const id = year.id; // always safe, no `??` chains in components

export const getFiscalYearId = (year) =>
  year?.fiscal_year_id ?? year?.id ?? null;

export const getPeriodId = (period) =>
  period?.period_id ?? period?.id ?? null;

// Generic fallback for other ledger entities (accounts, journals, ...).
export const getLedgerId = (item, keys = []) => {
  if (!item) return null;
  for (const key of [...keys, 'id']) {
    if (item[key] !== undefined && item[key] !== null) return item[key];
  }
  return null;
};

// --- Chart of Accounts hierarchy ---
// Prisma keys: account_class_id, account_type_id, sub_type_id, account_id.
// Journal entries: entry_id (detail includes journal_entry_id aliases).
// Balances: balance_id. List responses keep native keys; `.id` is canonical.
export const getAccountClassId = (c) =>
  c?.account_class_id ?? c?.id ?? null;

export const getAccountTypeId = (t) =>
  t?.account_type_id ?? t?.id ?? null;

export const getSubTypeId = (s) =>
  s?.sub_type_id ?? s?.sub_typeId ?? s?.id ?? null;

export const getAccountId = (a) =>
  a?.account_id ?? a?.id ?? null;

export const getJournalId = (e) =>
  e?.journal_entry_id ?? e?.entry_id ?? e?.id ?? null;

export const getJournalLineId = (l) =>
  l?.journal_entry_line_id ?? l?.line_id ?? l?.id ?? null;

export const getBalanceId = (b) =>
  b?.balance_id ?? b?.id ?? null;

export const getReconId = (r) =>
  r?.recon_id ?? r?.reconciliation_id ?? r?.id ?? null;

export const getReconLineId = (l) =>
  l?.line_id ?? l?.recon_line_id ?? l?.id ?? null;

const normalizeList = (list, normalize) =>
  (Array.isArray(list) ? list : []).map(normalize);

export const normalizeAccountClass = (c) =>
  !c ? c : { ...c, id: getAccountClassId(c) };

export const normalizeAccountType = (t) =>
  !t ? t : { ...t, id: getAccountTypeId(t) };

export const normalizeSubType = (s) =>
  !s ? s : { ...s, id: getSubTypeId(s) };

export const normalizeAccount = (a) =>
  !a ? a : { ...a, id: getAccountId(a) };

export const normalizeJournal = (e) => {
  if (!e) return e;
  return {
    ...e,
    id: getJournalId(e),
    lines: Array.isArray(e.lines) ? e.lines.map(normalizeJournalLine) : e.lines,
  };
};

export const normalizeJournalLine = (l) =>
  !l ? l : { ...l, id: getJournalLineId(l) };

export const normalizeBalance = (b) =>
  !b ? b : { ...b, id: getBalanceId(b) };

export const normalizeReconLine = (l) =>
  !l ? l : { ...l, id: getReconLineId(l) };

export const normalizeRecon = (r) => {
  if (!r) return r;
  return {
    ...r,
    id: getReconId(r),
    lines: Array.isArray(r.lines) ? r.lines.map(normalizeReconLine) : r.lines,
  };
};

export const normalizeAccountClasses = (list = []) =>
  normalizeList(list, normalizeAccountClass);

export const normalizeAccountTypes = (list = []) =>
  normalizeList(list, normalizeAccountType);

export const normalizeSubTypes = (list = []) =>
  normalizeList(list, normalizeSubType);

export const normalizeAccounts = (list = []) =>
  normalizeList(list, normalizeAccount);

export const normalizeJournals = (list = []) =>
  normalizeList(list, normalizeJournal);

export const normalizeJournalLines = (list = []) =>
  normalizeList(list, normalizeJournalLine);

export const normalizeBalances = (list = []) =>
  normalizeList(list, normalizeBalance);

export const normalizeRecons = (list = []) =>
  normalizeList(list, normalizeRecon);

export const normalizeFiscalYear = (year) => {
  if (!year) return year;
  const id = getFiscalYearId(year);
  return {
    ...year,
    id,
    periods: Array.isArray(year.periods)
      ? year.periods.map(normalizeFiscalPeriod)
      : year.periods,
  };
};

export const normalizeFiscalPeriod = (period) => {
  if (!period) return period;
  const id = getPeriodId(period);
  const fiscalYearId =
    period.fiscal_year_id ?? period.fiscalYearId ?? period.year_id ?? null;
  return { ...period, id, fiscal_year_id: fiscalYearId ?? period.fiscal_year_id };
};

export const normalizeFiscalYears = (list = []) =>
  (Array.isArray(list) ? list : []).map(normalizeFiscalYear);

export const normalizeFiscalPeriods = (list = []) =>
  (Array.isArray(list) ? list : []).map(normalizeFiscalPeriod);
